/**
 * ChangelogEntry — single event row in the changelog feed.
 *
 * Renders an icon, summary text, timestamp, and expandable detail section.
 * Icon and accent color are determined by event_type.
 */

import { useState } from "react";
import { Badge, Box, Flex, Text } from "@radix-ui/themes";
import type { ChangeEvent } from "../../api/changelog";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useUserLookup } from "../../hooks/useUserLookup";

const EVENT_CONFIG: Record<
  string,
  { icon: string; color: "amber" | "red" | "green" | "blue" | "purple" | "gray" }
> = {
  parameter_changed: { icon: "\u2699", color: "amber" },
  staleness_propagated: { icon: "\u26A0", color: "red" },
  staleness_cleared: { icon: "\u2713", color: "green" },
  data_imported: { icon: "\u2193", color: "blue" },
  runs_created: { icon: "+", color: "blue" },
  wing_area_detected: { icon: "\u25F0", color: "purple" },
  wing_area_confirmed: { icon: "\u2713", color: "green" },
  branch_merged: { icon: "\u2387", color: "gray" },
  commit_created: { icon: "\u25CB", color: "gray" },
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DetailView({ detail, eventType }: { detail: Record<string, unknown>; eventType: string }) {
  if (eventType === "parameter_changed") {
    return (
      <Flex direction="column" gap="1">
        {detail.old_value != null && (
          <Text size="1" color="gray">
            <span style={{ textDecoration: "line-through" }}>{String(detail.old_value)}</span>
            {" \u2192 "}
            <span style={{ fontWeight: 500 }}>{String(detail.new_value)}</span>
            {detail.units ? ` ${String(detail.units)}` : ""}
          </Text>
        )}
        {Boolean(detail.reason) && <Text size="1" color="gray">Reason: {String(detail.reason)}</Text>}
        {(detail.invalidated_count as number) > 0 && (
          <Text size="1" color="red">{String(detail.invalidated_count)} nodes invalidated</Text>
        )}
      </Flex>
    );
  }

  if (eventType === "staleness_propagated") {
    const paths = (detail.invalidated_paths as string[]) || [];
    return (
      <Flex direction="column" gap="1" style={{ maxHeight: 120, overflow: "auto" }}>
        {paths.map((p) => (
          <Text key={p} size="1" color="gray" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
            {p}
          </Text>
        ))}
        {paths.length === 50 && <Text size="1" color="gray">... and more</Text>}
      </Flex>
    );
  }

  const entries = Object.entries(detail).filter(([, v]) => v != null);
  if (!entries.length) return null;
  return (
    <Flex direction="column" gap="1">
      {entries.map(([k, v]) => (
        <Text key={k} size="1" color="gray">
          <span style={{ fontWeight: 500 }}>{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
        </Text>
      ))}
    </Flex>
  );
}

interface Props {
  event: ChangeEvent;
  /** When true, renders a left accent bar indicating this event relates to the selected node. */
  highlighted?: boolean;
}

export function ChangelogEntry({ event, highlighted = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  const config = EVENT_CONFIG[event.event_type] ?? { icon: "\u2022", color: "gray" as const };
  const { projectId } = useProvenanceContext();
  const resolveUser = useUserLookup(projectId);

  return (
    <Box
      p="3"
      onClick={() => setExpanded(!expanded)}
      style={{
        borderRadius: "var(--radius-2)",
        border: highlighted ? "1px solid var(--accent-a6)" : "1px solid var(--gray-a4)",
        borderLeft: highlighted ? "3px solid var(--accent-9)" : undefined,
        background: highlighted ? "var(--accent-a2)" : "var(--color-surface)",
        cursor: "pointer",
      }}
    >
      <Flex direction="column" gap="1">
        <Flex align="center" gap="2">
          <Text size="2" style={{ width: 20, textAlign: "center", flexShrink: 0 }}>
            {config.icon}
          </Text>
          <Text size="2" style={{ flex: 1 }}>
            {event.summary}
          </Text>
          <Badge size="1" color={config.color}>
            {event.event_type.replace(/_/g, " ")}
          </Badge>
        </Flex>

        <Flex align="center" gap="3" ml="5">
          <Text size="1" color="gray">{resolveUser(event.created_by)}</Text>
          <Text size="1" color="gray">{formatTime(event.created_at)}</Text>
          {event.affected_count > 0 && (
            <Text size="1" color="gray">{event.affected_count} affected</Text>
          )}
        </Flex>

        {expanded && event.detail && Object.keys(event.detail).length > 0 && (
          <Box mt="2" ml="5" p="2" style={{ background: "var(--gray-a2)", borderRadius: "var(--radius-1)" }}>
            <DetailView detail={event.detail} eventType={event.event_type} />
          </Box>
        )}
      </Flex>
    </Box>
  );
}
